// src/features/task/form/PropertyPill.tsx
// A task property, presented the way a person thinks about it: a compact pill
// that reads "Assignee" when nothing is chosen and "Anna Petrova" once it is.
// The real control lives in a popover behind it, so eight properties cost eight
// short words on screen instead of eight labelled rows.
//
// One primitive, three looks: `pill` for a compact inline chip, `quiet` for a
// text-only menu trigger (Start from template), and `row` for the always-open
// properties panel, where the label is rendered outside by `PropertyRow` and
// the trigger is a full-width value that reads "не задано" while empty.
//
// Dismissal is deliberately portal-aware. Several of the controls placed inside
// (UserPicker, TagPicker) render their own dropdown into document.body, so a
// naive "click outside closes" would eat the very click that picks a value.
// Anything landing outside #root is another popover layer, not the page.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@core/ui';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { Icon, FORM_ICONS } from './icons';
import { propertyTriggerClass } from './propertyTrigger';

const DEFAULT_POPOVER_WIDTH = 288;
const POPOVER_MAX_HEIGHT = 380;

/** True when a click landed in a portal layer that is not the page itself. */
function inOtherPortalLayer(target: Node): boolean {
  const root = document.getElementById('root');
  return !!root && document.body.contains(target) && !root.contains(target);
}

export interface PropertyPillProps {
  /** Stroke path from FORM_ICONS. */
  icon: string;
  /** Extra classes for the icon — used to carry a semantic colour. */
  iconClassName?: string;
  /** A colour swatch shown instead of the icon (task type colours). */
  swatch?: string;
  /** What the property is. Shown alone, muted, while nothing is chosen. */
  label: string;
  /** The chosen value. When present it replaces the label on the pill. */
  value?: string | null;
  /** Marks the pill invalid; the message is rendered by the caller. */
  invalid?: boolean;
  /** DOM id of that message, so the pill announces it rather than only reddening. */
  errorId?: string;
  /** Shows a required marker beside the label. */
  required?: boolean;
  disabled?: boolean;
  /**
   * What the `row` variant shows while nothing is chosen. The row keeps its
   * place in the panel and says "не задано" — it never collapses, because a
   * property that can be set is information even when it is not set yet.
   */
  emptyText?: string;
  /** Heading inside the popover. Falls back to `label`. */
  popoverTitle?: string;
  /** One short line under the popover heading — never on the page. */
  popoverHint?: string;
  popoverWidth?: number;
  variant?: 'pill' | 'quiet' | 'row';
  className?: string;
  'data-field-error'?: string;
  /** Popover body. `close` dismisses it and returns focus to the trigger. */
  children: (close: () => void) => ReactNode;
}

export function PropertyPill({
  icon,
  iconClassName,
  swatch,
  label,
  value,
  invalid,
  errorId,
  required,
  disabled,
  popoverTitle,
  popoverHint,
  popoverWidth = DEFAULT_POPOVER_WIDTH,
  variant = 'pill',
  emptyText,
  className,
  children,
  ...rest
}: PropertyPillProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const popoverId = useId();

  const hasValue = Boolean(value && value.trim());

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);


  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The trigger toggles itself — closing here would fight its own click.
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      if (inOtherPortalLayer(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only ours if focus is still inside it; a nested picker gets Escape first.
      if (!popoverRef.current?.contains(document.activeElement)) return;
      close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    const raf = window.requestAnimationFrame(() => {
      const node = popoverRef.current;
      const focusable = node?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable ?? node)?.focus();
    });

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(raf);
    };
  }, [open, close]);

  /** Required and still empty — marked before Create is ever pressed. */
  const missingRequired = Boolean(required) && !hasValue && !invalid;

  const triggerClass = propertyTriggerClass(variant, { hasValue, open, invalid, missingRequired });

  return (
    <>
      <button
        {...rest}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-describedby={invalid ? errorId : undefined}
        // The pill shows the value; the label is what the value *is*, so both
        // belong in the accessible name. An empty row still announces what it
        // would set, and that it is unset.
        aria-label={hasValue ? `${label}: ${value}` : emptyText ? `${label}: ${emptyText}` : label}
        className={cn(triggerClass, className)}
      >
        {swatch ? (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
            style={{ backgroundColor: swatch }}
            aria-hidden="true"
          />
        ) : (
          <Icon d={icon} className={cn('h-3.5 w-3.5 shrink-0', iconClassName)} />
        )}
        <span className={cn('truncate', variant === 'row' && 'min-w-0 flex-1')}>
          {hasValue ? value : variant === 'row' ? (emptyText ?? label) : label}
        </span>
        {/* In a row the asterisk belongs beside the label, which PropertyRow owns. */}
        {required && !hasValue && variant !== 'row' && (
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        )}
        {(variant === 'quiet' || variant === 'row') && (
          <Icon
            d={FORM_ICONS.chevronDown}
            className="h-3.5 w-3.5 shrink-0 opacity-50 transition-opacity"
          />
        )}
      </button>

      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={close}
        ref={popoverRef}
        id={popoverId}
        role="dialog"
        ariaLabel={popoverTitle ?? label}
        // Dismissal stays local: several controls inside render their own
        // portals, and a click in those must not count as outside.
        closeOnOutsideClick={false}
        closeOnEscape={false}
        width={Math.min(popoverWidth, window.innerWidth - 16)}
        maxHeight={POPOVER_MAX_HEIGHT}
        gap={6}
        className="flex flex-col gap-2 p-3 focus:outline-none"
      >
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {popoverTitle ?? label}
          </p>
          {popoverHint && <p className="text-xs text-muted-foreground">{popoverHint}</p>}
          {children(close)}
      </AnchoredMenu>
    </>
  );
}

// ---------------------------------------------------------------------------
// A list of choices inside a popover
// ---------------------------------------------------------------------------

export interface PillOption {
  value: string;
  label: string;
  /** Secondary line — where a template comes from, a category code, … */
  hint?: string;
  icon?: string;
  iconClassName?: string;
  swatch?: string;
}

export interface PillOptionListProps {
  options: PillOption[];
  value: string;
  onSelect: (value: string) => void;
  /** Accessible name — the popover heading is decorative text, not a label. */
  ariaLabel: string;
}

/**
 * Single-choice list. Real buttons in a radiogroup: Tab reaches the group,
 * arrows move within it, Enter and Space pick — no custom focus trickery and
 * no reliance on hover.
 */
export function PillOptionList({ options, value, onSelect, ariaLabel }: PillOptionListProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const move = (index: number, delta: number) => {
    const next = (index + delta + options.length) % options.length;
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[next]?.focus();
  };

  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div ref={groupRef} role="radiogroup" aria-label={ariaLabel} className="-mx-1 flex flex-col">
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value || `__${index}`}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => onSelect(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                move(index, -1);
              }
            }}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50',
            )}
          >
            {option.swatch ? (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                style={{ backgroundColor: option.swatch }}
                aria-hidden="true"
              />
            ) : option.icon ? (
              <Icon
                d={option.icon}
                className={cn('h-4 w-4 shrink-0', option.iconClassName ?? 'text-muted-foreground')}
              />
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate">{option.label}</span>
              {option.hint && (
                <span className="block truncate text-xs text-muted-foreground">{option.hint}</span>
              )}
            </span>
            {selected && <Icon d={FORM_ICONS.check} className="h-3.5 w-3.5 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
